import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, Space, Spin, Tabs, Tag } from 'antd';
import styled from 'styled-components';
import Button from '../../components/Button';
import Panel from '../../components/outline/Panel';
import { useAuth } from '../../hooks/common/useAuth';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from '../../constants/service/common/userPermissions';
import { PROMOTION_SCHEDULE_LIST_URL_PATH } from '../../constants/service/common/urlConstants';
import { ScheduleDetailPanel } from '../../views/promotion/schedule/components/ScheduleDetailPanel';
import { ScheduleFormModal } from '../../views/promotion/schedule/components/ScheduleFormModal';
import { ScheduleLogList } from '../../views/promotion/schedule/components/ScheduleLogList';
import { STATUS_LABEL, TRACK_META } from '../../views/promotion/schedule/constants';
import { promotionScheduleQuery } from '../../views/promotion/schedule/queries';
import { toDatePart } from '../../views/promotion/schedule/utils';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  .title {
    font-size: 20px;
    font-weight: 700;
  }

  .meta {
    margin-top: 6px;
    color: #5b5f66;
  }
`;

const StyledSpinWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const TAB_KEY = {
  INFO: 'info',
  LOGS: 'logs',
} as const;

/**
 * 프로모션 상세 컨테이너 (AMZUWEB-682).
 *
 * **처음부터 탭 구조로 간다.** 지금 담을 것은 두 장뿐이지만, 설계 문서 §1 이
 * *"스케줄 항목이 기획전·기획안의 부모"* 라고 못 박았고 R2 기획안·R5 성과가 여기 붙는다.
 * 탭 한 장짜리로 시작해도 구조를 먼저 잡아두면 그때 화면을 다시 뒤집지 않아도 된다.
 *
 * ```
 * [기본 정보] [변경 이력]          ← 지금
 *               ↑ [기획안] [성과]   ← R2 / R5
 * ```
 *
 * 🔴 **폼을 여기에 복제하지 않는다.** 수정은 타임라인이 쓰는 `ScheduleFormModal` 을 그대로 연다 —
 * 검증·연결 UI 가 두 벌이 되면 한쪽만 고치는 날이 온다.
 */
export const PromotionScheduleDetailContainer = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();

  /**
   * 되돌아갈 리스트. 리스트에서 행을 눌러 들어왔으면 **그때 보던 URL**(필터·정렬·페이지 포함)이
   * `state` 에 실려 온다. 링크·헤더 숏컷·새로고침으로 직접 들어온 경우엔 없으므로 기본 리스트로 간다.
   *
   * 화면에 "리스트로" 버튼이 세 군데(정상·잘못된 id·조회 실패) 있는데 **전부 이 함수를 쓴다** —
   * 한 곳만 상수를 직접 쓰면 그 경로로 빠져나간 사람만 조건을 잃는다.
   */
  const listUrl = (location.state as { listUrl?: string } | null)?.listUrl;
  const goToList = (): void => navigate(listUrl ?? PROMOTION_SCHEDULE_LIST_URL_PATH);

  /**
   * 삭제하면 이 화면이 보고 있던 대상이 사라지므로 리스트로 떠난다.
   *
   * 캐시 정리(상세·이력 제거)는 **뮤테이션이 한다** — 여기서 하면 이미 끝난 재조회 뒤라
   * 늦고, 타임라인에서 삭제한 경우엔 아예 돌지 않는다.
   */
  const handleDeleted = (): void => goToList();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(TAB_KEY.INFO);

  const canUpdate = can(PERMISSION_ACTIONS.UPDATE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);
  const canDelete = can(PERMISSION_ACTIONS.DELETE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);

  const scheduleId = Number(id);
  const isValidId = Number.isInteger(scheduleId) && scheduleId > 0;

  const {
    data: schedule,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    ...promotionScheduleQuery.detail(scheduleId),
    enabled: isValidId,
  });

  /**
   * 주소에 숫자가 아닌 값이 들어온 경우. 서버까지 보내지 않고 여기서 끊는다 —
   * 빈 화면 대신 안내를 띄우고 리스트로 돌아갈 길을 준다.
   */
  if (!isValidId) {
    return (
      <StyledContainer>
        <Alert
          type="error"
          showIcon={true}
          message="잘못된 주소입니다."
          description="프로모션 번호가 올바르지 않습니다."
          action={<Button text="리스트로" onClick={goToList} />}
        />
      </StyledContainer>
    );
  }

  if (isLoading) {
    return (
      <StyledSpinWrap>
        <Spin />
      </StyledSpinWrap>
    );
  }

  /**
   * 🔴 **"없는 항목" 과 "조회 실패" 를 가른다.**
   *
   * 둘 다 `isError` 라서 한 문구로 묶으면, 서버가 잠깐 죽었거나 네트워크가 끊긴 경우에도
   * *"삭제되었거나 존재하지 않는 번호"* 라고 말하게 된다. 사용자는 멀쩡한 항목을
   * 지워진 걸로 알고 되돌아가며, 다시 시도할 생각도 못 한다.
   *
   * 없는 id·삭제된 id 는 서버가 **400**(`PROMOTIONSCHEDULE012`)을 준다(404 가 아니다).
   * 숫자가 아닌 id 는 위에서 이미 걸러내므로, 여기 도달한 400 은 "그 항목이 없다" 로 읽어도 된다.
   * 그 밖(5xx·타임아웃·네트워크)은 **일시적 실패**라 다시 시도할 길을 준다.
   */
  if (isError || !schedule) {
    const isNotFound = isAxiosError(error) && error.response?.status === 400;

    return (
      <StyledContainer>
        {isNotFound ? (
          <Alert
            type="warning"
            showIcon={true}
            message="프로모션을 찾을 수 없습니다."
            description="삭제되었거나 존재하지 않는 번호입니다."
            action={<Button text="리스트로" onClick={goToList} />}
          />
        ) : (
          <Alert
            type="error"
            showIcon={true}
            message="프로모션을 불러오지 못했습니다."
            description="일시적인 오류일 수 있습니다. 잠시 후 다시 시도해 주세요."
            action={
              <Space>
                <Button
                  color="primary"
                  text={isFetching ? '불러오는 중...' : '다시 시도'}
                  disabled={isFetching}
                  onClick={() => refetch()}
                />
                <Button text="리스트로" onClick={goToList} />
              </Space>
            }
          />
        )}
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <div>
          <div className="title">{schedule.title}</div>
          <Space className="meta" size={8}>
            <Tag color={TRACK_META[schedule.track]?.color}>{TRACK_META[schedule.track]?.label}</Tag>
            <span>{STATUS_LABEL[schedule.status]}</span>
            <span>
              {toDatePart(schedule.startDate)} ~ {toDatePart(schedule.endDate)}
            </span>
          </Space>
        </div>

        <Space>
          <Button text="리스트로" onClick={goToList} />
          {canUpdate && <Button color="primary" text="수정" onClick={() => setIsEditOpen(true)} />}
        </Space>
      </StyledHeader>

      {/*
        탭 내용을 Panel 로 감싼다 — 어드민 상세의 표준 봉투다(관리자 상세·기획전 편집 등 114곳).
        Panel 없이 두면 흰 배경에 내용만 떠 있어 같은 제품의 다른 화면처럼 보인다.
      */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: TAB_KEY.INFO,
            label: '기본 정보',
            children: (
              <Panel>
                <div className="panel-header">
                  <span className="title">기본 정보</span>
                </div>
                <div className="panel-body">
                  <ScheduleDetailPanel schedule={schedule} />
                </div>
              </Panel>
            ),
          },
          {
            key: TAB_KEY.LOGS,
            label: '변경 이력',
            children: (
              <Panel>
                <div className="panel-header">
                  <span className="title">변경 이력</span>
                </div>
                <div className="panel-body">
                  <ScheduleLogList promotionScheduleId={schedule.id} />
                </div>
              </Panel>
            ),
          },
        ]}
      />

      {isEditOpen && (
        <ScheduleFormModal
          schedule={schedule}
          canUpdate={canUpdate}
          canDelete={canDelete}
          /* 여기가 이미 상세 화면이다 — 같은 곳으로 가는 버튼을 또 띄우지 않는다 */
          showDetailLink={false}
          onDeleted={handleDeleted}
          onClose={() => setIsEditOpen(false)}
        />
      )}
    </StyledContainer>
  );
};
